import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import {
  TimeoutError,
  catchError,
  combineLatest,
  filter,
  from,
  map,
  of,
  retry,
  switchMap,
  throwError,
  timeout,
} from 'rxjs';
import { AccountService } from 'src/app/shared/services/account.service';
import { AuthService } from 'src/app/shared/services/auth.service';

/**
 * Route guard to validate a client has actually given their consent.
 * If they have given it, allow them to continue to the page. If not,
 * redirect them back to a page to give the consent.
 *
 * @param _
 * @param state
 * @returns `true` if they should proceed as normal, or a `URL Tree` if navigating to a specific page
 */
export const verifyCosumerConsentGuard: CanActivateFn = (_: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const router: Router = inject(Router);
  const authService: AuthService = inject(AuthService);
  const accountService: AccountService = inject(AccountService);

  const authUserIsLoggedIn$ = authService.isUserLoggedIn$.pipe(
    /**
     * This is to ensure that users are kept logged in if there is a token saved in the storage.
     *
     * The isUserLoggedIn$ fires a FALSE value when the token is refreshed during application load. So to prevent logging out the user,
     * it will instead check if there is an AuthService.user rather than the state of the token.
     *
     * This matches the condition in the UserGuard.
     */
    switchMap(() => of(authService.isUserLoggedIn())),
  );

  const fetchAccountUser$ = from(accountService.account()).pipe(
    catchError((error) => {
      console.error('[verifyCosumerConsentGuard] Failed to fetch account user', error);
      return throwError(() => error);
    }),

    // Rescue error response and retry once
    retry(1),
  );

  return combineLatest({
    isInitialised: authService.isInitialised$,
    isUserLoggedIn: authUserIsLoggedIn$,
  }).pipe(
    filter(({ isInitialised }) => isInitialised),
    timeout({
      first: 30000,
    }),
    // This result is cached by default. If consent is given on the BE during the flow, the user object will
    // be updated internally in the mobile app to prevent re-calling
    // Return null if not logged in, to handle in map
    switchMap(({ isUserLoggedIn }) => (isUserLoggedIn ? fetchAccountUser$ : of(null))),
    map((user) => {
      // Catches edge case where a user has not yet logged in. Since the UserGuard is async guards do not wait for the previous to finish
      // execution. Leave the more complex checking for the UserGuard.
      // Note that if a user returns as null, it means they are not logged in at this step in the code
      // as the switchMap above didnt call the accountService.
      if (!user) return router.createUrlTree(['/login']);

      // Allow access if:
      // - The user is not a consumer
      // - The user is a primary user
      // - The client has their consent given
      const isConsumer = user.accountType === 'Consumer';
      if (!isConsumer || !user.coordinator || user.consentToCollectSensitiveInformation) return true;

      // If we got here, it means a client is not given consent, but is logged in, in which case we want to send them to provide consent
      return router.createUrlTree(['/consumer-consent']);
    }),
    catchError((error) => {
      // Handling when `timeout` throws a error
      if (error instanceof TimeoutError) {
        console.error('[verifyCosumerConsentGuard] Auth Service failed to initialise within 5 seconds of app loading.');
        // Just stop execution of the routing, something has gone wrong elsewhere in the app if the
        // auth service doesnt initialise. We dont have a error screen, so send them here to give some feedback.
        return of(router.createUrlTree(['/maintenance-downtime']));
      }
      // The API call via `.account` has thrown a error, log
      console.error('[verifyCosumerConsentGuard] Error in setting up verify consumer consent guard', error);

      return of(true);
    }),
  );
};
